import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { Activity, Page } from '../../lib/types';
import { ACTIVITY_LABEL, formatDateTime, titleCase } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  Loading,
  SectionNote,
  Select,
  Textarea,
  useToast,
} from '../../components/ui';

/**
 * §13 Activity timeline.
 *
 * Entries carry their own sensitivity and are filtered server-side, so a
 * restricted leadership decision is simply not in a CSM's timeline rather than
 * showing as a redacted row. Where an entry came from a source, the fact of the
 * source is shown; its content needs `view_source`.
 */
export function AccountActivityTab({ accountId }: { accountId: string }) {
  const { me } = useSession();
  const [type, setType] = useState('');
  const [adding, setAdding] = useState(false);

  const query = useQuery({
    queryKey: ['activity', accountId, type],
    queryFn: () =>
      api.get<Page<Activity>>(`/accounts/${accountId}/activity`, {
        activityType: type || undefined,
        limit: 100,
      }),
  });

  const activities = query.data?.data ?? [];

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <Select value={type} onChange={(event) => setType(event.target.value)} className="w-48">
          <option value="">All activity</option>
          <option value="meeting">Meetings</option>
          <option value="email_inbound">Email received</option>
          <option value="email_outbound">Email sent</option>
          <option value="note">Notes</option>
          <option value="decision">Decisions</option>
          <option value="commitment_created">Commitments recorded</option>
          <option value="commitment_completed">Commitments completed</option>
          <option value="system_event">System events</option>
        </Select>
        <Button size="sm" variant="primary" onClick={() => setAdding(true)}>
          Add a note
        </Button>
      </div>

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}

      {query.data && activities.length === 0 && (
        <EmptyState title="Nothing recorded" description="No activity matches this filter." />
      )}

      {activities.length > 0 && (
        <Card padded={false}>
          <ol className="divide-y divide-border">
            {activities.map((activity) => (
              <li key={activity.id} className="flex gap-3 px-4 py-3">
                <div className="w-32 shrink-0 pt-0.5">
                  <p className="text-xs font-medium text-ink-2">
                    {formatDateTime(activity.occurredAt)}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted">
                    {ACTIVITY_LABEL[activity.activityType] ?? titleCase(activity.activityType)}
                  </p>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-medium text-ink">{activity.title}</p>
                    {activity.visibilityClassification !== 'STANDARD' && (
                      <Badge tone="neutral" dot={false}>
                        {titleCase(activity.visibilityClassification.toLowerCase())}
                      </Badge>
                    )}
                  </div>
                  {activity.summary && (
                    <p className="mt-1 text-sm whitespace-pre-wrap text-ink-2">
                      {activity.summary}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted">
                    {activity.actorName ?? 'System'}
                    {activity.sourceType && (
                      <>
                        {' · '}
                        {titleCase(activity.sourceType)}
                        {activity.sourceSubject ? `: ${activity.sourceSubject}` : ''}
                        {!activity.sourceSubject && !me.capabilities.canViewSourceEvidence && (
                          <span className="text-muted"> (source content restricted)</span>
                        )}
                      </>
                    )}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Card>
      )}

      <div className="mt-3">
        <SectionNote>
          Email, calendar and meeting-transcript entries are recorded manually until the connectors
          arrive in Phase 4–5. After that they appear here automatically, with their source
          attached.
        </SectionNote>
      </div>

      {adding && <AddNoteDialog accountId={accountId} onClose={() => setAdding(false)} />}
    </>
  );
}

function AddNoteDialog({ accountId, onClose }: { accountId: string; onClose: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [activityType, setActivityType] = useState('note');
  const [visibility, setVisibility] = useState('STANDARD');

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/accounts/${accountId}/activity`, {
        title,
        summary: summary || undefined,
        activityType,
        visibilityClassification: visibility,
      }),
    onSuccess: () => {
      toast('Added to the timeline');
      void queryClient.invalidateQueries({ queryKey: ['activity', accountId] });
      void queryClient.invalidateQueries({ queryKey: ['account-summary', accountId] });
      onClose();
    },
  });

  // Only offer the clearances the caller actually holds.
  const visibilityOptions = me.sensitivities.length > 0 ? me.sensitivities : ['STANDARD'];

  return (
    <Dialog
      open
      onClose={onClose}
      title="Add to the timeline"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!title.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Saving…' : 'Add'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <Select
              value={activityType}
              onChange={(event) => setActivityType(event.target.value)}
            >
              <option value="note">Note</option>
              <option value="decision">Decision</option>
              <option value="meeting">Meeting</option>
              <option value="email_outbound">Email sent</option>
              <option value="email_inbound">Email received</option>
            </Select>
          </Field>
          <Field
            label="Visibility"
            hint="Restricted entries are hidden from people without that clearance."
          >
            <Select value={visibility} onChange={(event) => setVisibility(event.target.value)}>
              {visibilityOptions.map((level) => (
                <option key={level} value={level}>
                  {titleCase(level.toLowerCase())}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Title" required>
          <Input value={title} onChange={(event) => setTitle(event.target.value)} />
        </Field>
        <Field label="Detail">
          <Textarea
            value={summary}
            onChange={(event) => setSummary(event.target.value)}
            rows={4}
          />
        </Field>
        {mutation.error && <ErrorNotice error={mutation.error} />}
      </div>
    </Dialog>
  );
}
